import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import moment from "moment";

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function distanceInMeters(origin, destination) {
  if (!origin || !destination) return Infinity;

  const R = 6371000;
  const toRad = (value) => (value * Math.PI) / 180;

  const dLat = toRad(destination.latitud - origin.latitud);
  const dLon = toRad(destination.longitud - origin.longitud);

  const lat1 = toRad(origin.latitud);
  const lat2 = toRad(destination.latitud);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export default function createLocationTrackerFirestore(config = {}) {
  const db = config.db;
  const coleccion = config.coleccion || "riskmann_registro_ubicacion_usuarios";

  if (!config.db) throw new Error("db (Firestore) es requerido");
  if (!config.coleccion) throw new Error("idUsuario es obligatorio");

  let idUsuario = config.idUsuario;
  let idEmpresa = config.idEmpresa || "";

  let watchId = null;
  let activo = false;
  let guardando = false;
  let ultimaPersistencia = 0;
  let ultimasCoordenadas = null;

  const geoOptions = {
    enableHighAccuracy:
      typeof config.enableHighAccuracy === "boolean"
        ? config.enableHighAccuracy
        : true,
    timeout: typeof config.timeout === "number" ? config.timeout : 15000,
    maximumAge:
      typeof config.maximumAge === "number" ? config.maximumAge : 10000,
  };

  const minUpdateMs =
    typeof config.minUpdateMs === "number" ? config.minUpdateMs : 30000;

  const minDistanceMeters =
    typeof config.minDistanceMeters === "number"
      ? config.minDistanceMeters
      : 15;

  function manageError(error) {
    if (typeof config.onError === "function") {
      config.onError(error);
      return;
    }
    console.warn("[locationTracker]", error);
  }

  function getDocumentRef() {
    return doc(db, coleccion, idUsuario);
  }

  function buildCoordinatesPayload(coords) {
    return {
      latitud: toNumber(coords.latitude, 0),
      longitud: toNumber(coords.longitude, 0),
      precision: toNumber(coords.accuracy, 0),
    };
  }

  function persist(coords) {
    const nuevasCoordenadas = buildCoordinatesPayload(coords);

    if (!ultimasCoordenadas) return true;

    const tiempoTranscurrido = Date.now() - ultimaPersistencia;
    const distancia = distanceInMeters(ultimasCoordenadas, nuevasCoordenadas);

    if (tiempoTranscurrido >= minUpdateMs) return true;
    if (distancia >= minDistanceMeters) return true;

    return false;
  }

  async function persistCoordinates(coords) {
    if (!coords || guardando) return false;

    guardando = true;

    try {
      const coordenadas = buildCoordinatesPayload(coords);
      const ref = getDocumentRef();
      const snapshot = await getDoc(ref);

      if (snapshot.exists()) {
        await updateDoc(ref, {
          idEmpresa: idEmpresa,
          coordenadas,
          "registro.estado": "Editado",
          "registro.actualizacion": serverTimestamp(),
          "registro.filtro": moment().format("YYYY-MM-DD"),
        });
      } else {
        await setDoc(ref, {
          id: idUsuario,
          idEmpresa: idEmpresa,
          coordenadas,
          registro: {
            estado: "Creado",
            creacion: serverTimestamp(),
            actualizacion: serverTimestamp(),
            filtro: moment().format("YYYY-MM-DD"),
          },
        });
      }

      ultimaPersistencia = Date.now();
      ultimasCoordenadas = coordenadas;

      return true;
    } catch (error) {
      manageError(error);
      return false;
    } finally {
      guardando = false;
    }
  }

  function onPosition(position) {
    if (!position || !position.coords) return;

    if (!persist(position.coords)) {
      return;
    }

    persistCoordinates(position.coords);
  }

  function getCurrentLocation() {
    return new Promise((resolve, reject) => {
      if (
        typeof window === "undefined" ||
        typeof navigator === "undefined" ||
        !navigator.geolocation
      ) {
        const error = new Error(
          "La geolocalización no está soportada en este navegador",
        );
        manageError(error);
        reject(error);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          await persistCoordinates(position.coords);
          resolve(position);
        },
        (error) => {
          manageError(error);
          reject(error);
        },
        geoOptions,
      );
    });
  }

  function start() {
    if (activo) return;

    if (
      typeof window === "undefined" ||
      typeof navigator === "undefined" ||
      !navigator.geolocation
    ) {
      manageError(
        new Error("La geolocalización no está disponible en este entorno"),
      );
      return;
    }

    // Captura inicial
    getCurrentLocation().catch(() => {});

    // Seguimiento continuo
    watchId = navigator.geolocation.watchPosition(
      onPosition,
      manageError,
      geoOptions,
    );
  }

  function stop(reason = "manual-stop") {
    if (
      watchId !== null &&
      typeof navigator !== "undefined" &&
      navigator.geolocation
    ) {
      navigator.geolocation.clearWatch(watchId);
    }

    watchId = null;
    activo = false;
  }

  function getState() {
    return {
      activo,
      idUsuario,
      idEmpresa,
      ultimaPersistencia,
      ultimasCoordenadas,
      watchId,
    };
  }

  return {
    start,
    stop,
    getCurrentLocation,
    persistCoordinates,
    getState,
  };
}
